import { motion } from 'framer-motion';
import { FaCircle } from 'react-icons/fa';
import ExpandableCard from './ExpandableCard';

export default function Experience() {
  const experiences = [
    {
      title: 'Backend Developer',
      company: 'Antel',
      consultant: 'Epidata S.A',
      period: 'October 2025 - Present',
      description: `Backend Developer`,
      tasks: [
        'Design and develop server-side logic, database schemas, and backend services.',
        'Implement scalable, high-performance database solutions.',
        'Build and maintain RESTful APIs and web services for integration with frontend and external systems.',
        'Optimize system performance and database security.',
        'Apply security best practices and data protection measures.',
        'Collaborate with frontend teams to ensure effective integrations.',
        'Write unit and integration tests to ensure code quality.',
        'Troubleshoot backend performance and scalability issues.',
        'Manage the lifecycle of backend applications.',
        'Document solutions and processes to facilitate maintenance.'
      ],
      link: '',
      technologies: ['Java', 'Spring Boot', 'REST', 'PostgreSQL', 'Docker']
    },
    {
      title: 'Java Developer',
      company: 'Clip Connections',
      period: '2021 - September 2025',
      description: `Development of Java EE, Wildfly, Primefaces and JSF applications for efficient and effective purchasing and stock management for the Ministry of Tourism (MINTUR), and to facilitate the scheduling and booking of meeting rooms. Stack: Java 8 EE, Wildfly 21, Primefaces 11, Omnifaces 3.11, JSF 2.3 (Mojarra), JPA (Hibernate), PostgreSQL 9.6, Ant, Git.`,
      link: 'https://gitlab.com/carlosdelviento23',
      technologies: ['Java EE', 'Wildfly', 'Primefaces', 'JSF', 'JPA', 'PostgreSQL', 'Ant', 'Git']
    },
    {
      title: 'Java Swing Project',
      company: 'ORT University',
      period: '2024',
      description: `Multiplayer poker game system implemented with Java 19 and Swing. The system allows players to participate in poker tables, place bets and compete against each other. Main features: User management, table administration, betting system and statistics. Stack: Java 19, Swing, JDK 19, Apache Ant, Git.`,
      link: 'https://gitlab.com/javaswingprojects/sistemapoker',
      technologies: ['Java 19', 'Swing', 'JDK 19', 'Apache Ant', 'Git']
    },
    {
      title: 'C# Workshop',
      company: 'ORT University',
      period: '2023',
      description: `Developing a stationery supplies order management system in C# with .NET Core 8. Allows businesses to manage orders, customers, items, and more. Stack: C#, .NET Core 8, Entity Framework Core 5, SQL Server, Git, GitHub, Visual Studio Community 2022 Preview, Postman.`,
      link: 'https://gitlab.com/csharpwebprojects/Papeleria',
      technologies: ['C#', '.NET Core 8', 'Entity Framework', 'SQL Server', 'Git', 'GitHub']
    },
    {
      title: 'Javascript Workshop',
      company: 'ORT University',
      period: '2023',
      description: `Development of a population census system in a given geographical area. The census will be carried out by collecting demographic and socioeconomic data from the inhabitants of the area. Stack: HTML 5, CSS 3, JavaScript, Local Storage, JSON, AJAX, Bootstrap 4, jQuery 3.6, Git, GitHub, Visual Studio Code.`,
      link: 'https://censo-js-app.pages.dev/',
      technologies: ['HTML 5', 'CSS 3', 'JavaScript', 'Bootstrap 4', 'jQuery', 'Git', 'GitHub']
    },
    {
      title: 'UTU Degree Project',
      company: 'ESI',
      period: '2022 - 2023',
      description: `Development of a backend system in Laravel to manage the internal operations of the entire system, an integral part of a larger system called Reasix IT Solutions to manage the company's infrastructure and sporting events. Stack: Laravel 8, Livewire, PHP 7.4, SqlServer, Bootstrap 4, Git, GitHub, Visual Studio Code, Docker, Docker Compose, PHPUnit.`,
      link: 'https://gitlab.com/laravelwebprojects/backoffice',
      technologies: ['Laravel 8', 'Livewire', 'PHP 7.4', 'SQL Server', 'Bootstrap 4', 'Docker', 'PHPUnit']
    },
    {
      title: 'Java Developer',
      company: 'Banca de Quinielas',
      period: '2019',
      description: `Java Developer at Banca de Quinielas in Montevideo. I worked on the Raspaditas Virtual project, where I implemented frontend screens to create reports using Java 8, HTML, RichFaces, Maven and the company's own framework. Stack: Java 8, HTML, RichFaces, Maven, Git.`,
      link: 'https://instagram.com/carlosdelviento.dev',
      technologies: ['Java 8', 'HTML', 'RichFaces', 'Maven', 'Git']
    },
    {
      title: 'Springboot integration in Java',
      company: 'ACCSA Test V2.0',
      period: '2019',
      description: `Java web application that integrates the Marvel API and queries the Marvel API to find all characters by id. Stack: Java 8, Spring Boot 3.2.0, Thymeleaf 3.0.12, Marvel API, Git, GitHub, Visual Studio Code.`,
      link: 'https://gitlab.com/spring_boot_projects/marvelAPI',
      technologies: ['Java 8', 'Spring Boot', 'Thymeleaf', 'Marvel API', 'Git', 'GitHub']
    }
  ];

  // Variantes para animaciones de Framer Motion
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section id='experience' className='container mx-auto w-full section undefined scroll-m-20 lg:max-w-4xl md:max-w-2xl' data-section='experience'>
      <motion.h2 
        className='flex gap-x-3 items-center mb-6 text-3xl font-semibold dark:text-white text'
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <svg className='size-8' width='24' height='24' viewBox='0 0 24 24' strokeWidth='2' stroke='currentColor' fill='none' strokeLinecap='round' strokeLinejoin='round'>
          <path stroke='none' d='M0 0h24v24H0z' fill='none'></path>
          <path d='M3 7m0 2a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v9a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z'></path>
          <path d='M8 7v-2a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v2'></path>
          <path d='M12 12l0 .01'></path>
          <path d='M3 13a20 20 0 0 0 18 0'></path>
        </svg>
        Work Experience
      </motion.h2>
      
      <motion.ol 
        className='relative mt-16'
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        {experiences.map((exp, index) => (
          <motion.li key={index} variants={itemVariants}>
            {/* Línea vertical */}
            <div className='relative mx-12 pb-12 grid before:absolute before:left-[-35px] before:block before:h-full before:border-l-2 before:border-black/20 dark:before:border-white/15 before:content- md:grid-cols-5 md:gap-10 md:space-x-4'>
              <div className='relative pb-12 md:col-span-2'>
                <div className='sticky top-0'>
                  {/* Círculo */}
                  <span className='text-accent -left-[42px] absolute rounded-full text-5xl'>
                    <FaCircle size={16} />
                  </span>
                  <h3 className='text-xl font-bold accent text-accent'>{exp.company}</h3>
                  <h4 className='text-xl font-semibold color-text dark:color-text text'>{exp.title}</h4>
                  {exp.consultant && (
                    <p className='text-sm text-gray-600 dark:text-gray-300'>Consultant: <span className='font-medium'>{exp.consultant}</span></p>
                  )}
                  <time className='p-0 m-0 text-sm accent dark:text-white/80'>{exp.period}</time>
                </div>
              </div>
              
              {/* Usando el componente ExpandableCard */}
              <div className="md:col-span-3">
                <ExpandableCard
                  title={exp.title}
                  subtitle={exp.technologies && exp.technologies.length > 0 ? `Stack: ${exp.technologies.slice(0, 3).join(", ")}${exp.technologies.length > 3 ? "..." : ""}` : undefined}
                  initiallyExpanded={index === 0}
                >
                  <div>
                    <p className="mb-4">{exp.description}</p>
                    {exp.tasks && exp.tasks.length > 0 && (
                      <div className="mt-4">
                        <h5 className="mb-2 font-medium text-accent">Responsibilities:</h5>
                        <ul className="list-disc pl-5 space-y-1">
                          {exp.tasks.map((task, tIdx) => (
                            <li key={tIdx}>{task}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Tecnologías */}
                    {exp.technologies && exp.technologies.length > 0 && (
                      <div className="mt-4">
                        <h5 className="mb-2 font-medium text-accent">Technologies:</h5>
                        <div className="flex flex-wrap gap-2">
                          {exp.technologies.map((tech, idx) => (
                            <span 
                              key={idx} 
                              className="px-2 py-1 text-xs rounded-md bg-[#232946] text-accent hover:bg-[#2e375a] transition-colors duration-200"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Enlace al proyecto */}
                    {exp.link && (
                      <a 
                        className="inline-flex items-center mt-4 text-lg font-medium text-accent dark:text-accent-light dark:hover:text-accent-light-hover hover:text-accent-hover" 
                        href={exp.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        View project
                        <svg className="ml-1" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="7" y1="17" x2="17" y2="7"></line>
                          <polyline points="7 7 17 7 17 17"></polyline>
                        </svg>
                      </a>
                    )}
                  </div>
                </ExpandableCard>
              </div>
            </div>
          </motion.li>
        ))}
      </motion.ol>
    </section>
  );
}
